import mapboxgl from 'mapbox-gl'
import { FC, useEffect, useRef } from 'react'
import { flushSync } from 'react-dom'
import { createRoot } from 'react-dom/client'

import { Colors } from 'src/components/theme'
import { useMap } from 'src/hooks/use-mapbox-map'
import { StateTooltip } from 'src/v2/components/activity-heatmap-map/state-tooltip'
import { StateSummary } from 'src/v2/views/owner-reveal/insights/owner-summary/activity-heatmap/types'
import * as atoms from 'src/v2/state/owner-reveal/activity-heatmap-map'
import { useCachedAtomValue } from 'src/utils/jotai'
import {
  StatesLayer as BaseStatesLayer,
  US_STATES_SOURCE_ID,
  US_STATES_FILL_LAYER_ID,
  StateFeatureProperties,
} from 'src/components/states-layer'

type StatesLayerProps = {
  entityId: string
  stateCode?: string
  onStateCodeChange?: (stateCode: string) => void
}

export const StatesLayer: FC<StatesLayerProps> = ({ entityId, onStateCodeChange, stateCode }) => {
  const map = useMap()
  const hoveredLayerId = useRef<string | undefined>()
  const features = useCachedAtomValue(atoms.activityHeatmapFeaturesById(entityId), [])

  const popup = useRef(
    new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      anchor: 'left',
      offset: [12, 24],
    })
  )
  const popupNode = document.createElement('div')
  const root = createRoot(popupNode)

  // TODO: Get startDate and endDate from filter atoms when implemented
  const renderStateTooltip = (
    summary: StateSummary,
    props: {
      onViewProperties: (state: string) => void
      onMouseEnter: () => void
      onMouseLeave: () => void
    }
  ) => (
    <StateTooltip
      state={summary.state}
      code={summary.code}
      startDate="01/03/2023"
      endDate="01/03/2024"
      purchased={summary.purchases}
      sold={summary.sales}
      totalParcels={summary.totalParcels}
      {...props}
    />
  )

  useEffect(() => {
    let mouseLeaveTimeout: NodeJS.Timeout | null = null

    const onMouseEnter = (ev: mapboxgl.MapMouseEvent & mapboxgl.EventData) => {
      if (ev.features?.length) {
        const feature = ev.features[0]
        const { stateCode: code, totalParcels, stateName } = feature.properties as StateFeatureProperties

        if (hoveredLayerId.current !== code) {
          if (hoveredLayerId.current) {
            map.setFeatureState({ source: US_STATES_SOURCE_ID, id: hoveredLayerId.current }, { hover: false })
          }

          hoveredLayerId.current = code
          map.setFeatureState({ source: US_STATES_SOURCE_ID, id: hoveredLayerId.current }, { hover: true })

          flushSync(() =>
            root.render(
              renderStateTooltip(
                {
                  percentage: 0,
                  purchases: { count: 0, total: 0 },
                  sales: { count: 0, total: 0 },
                  state: stateName,
                  total: 0,
                  totalParcels,
                  code,
                },
                { onViewProperties, onMouseEnter: clearMouseLeaveTimeout, onMouseLeave }
              )
            )
          )
          popup.current?.setLngLat(ev.lngLat).setDOMContent(popupNode).addTo(map)
        }
      }
    }

    const onMouseLeave = () => setMouseLeaveTimeout()

    const setMouseLeaveTimeout = () => {
      mouseLeaveTimeout = setTimeout(() => {
        if (hoveredLayerId.current) {
          map.setFeatureState({ source: US_STATES_SOURCE_ID, id: hoveredLayerId.current }, { hover: false })
        }
        popup.current?.remove()
        hoveredLayerId.current = undefined
      }, 150)
    }

    const clearMouseLeaveTimeout = () => {
      if (mouseLeaveTimeout !== null) {
        clearTimeout(mouseLeaveTimeout)
        mouseLeaveTimeout = null
      }
    }

    const onClick = (ev: mapboxgl.MapMouseEvent & mapboxgl.EventData) => {
      if (ev.features?.length) {
        const feature = ev.features[0]
        const { stateCode } = feature.properties as StateFeatureProperties
        onViewProperties(stateCode)
      }
    }

    const onViewProperties = (state: string) => {
      onStateCodeChange?.(state)
    }

    map.on('mousemove', US_STATES_FILL_LAYER_ID, onMouseEnter)
    map.on('mouseleave', US_STATES_FILL_LAYER_ID, onMouseLeave)
    map.on('click', US_STATES_FILL_LAYER_ID, onClick)

    return () => {
      map.off('mousemove', US_STATES_FILL_LAYER_ID, onMouseEnter)
      map.off('mouseleave', US_STATES_FILL_LAYER_ID, onMouseLeave)
      map.off('click', US_STATES_FILL_LAYER_ID, onClick)
    }
  }, [onStateCodeChange])

  return <BaseStatesLayer color={Colors.primaryRed700} features={features} selectedStateCode={stateCode} />
}
